import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, ClipboardCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { DeleteButton } from "@/components/DeleteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const EMPTY = { medication_name: "", unit: "", ndc: "", company_name: "" };

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { load(); }, []);
  async function load() {
    try {
      const [i, l] = await Promise.all([api.get("/inventory/items"), api.get("/inventory/logs")]);
      setItems(i.data); setLogs(l.data);
    } catch { /* */ }
  }

  async function addItem(e) {
    e.preventDefault(); setLoading(true);
    try { await api.post("/inventory/items", form); toast.success("Medication received."); setForm(EMPTY); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setLoading(false); }
  }

  async function completeLog() {
    try { await api.post("/inventory/logs"); toast.success("Inventory check logged & timestamped."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }

  async function delItem(id) {
    try { await api.delete(`/inventory/items/${id}`); toast.success("Item deleted."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete."); }
  }

  async function delLog(id) {
    try { await api.delete(`/inventory/logs/${id}`); toast.success("Inventory check deleted."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete."); }
  }

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold tracking-tight">Receiving Inventory</h1>
      <p className="mt-1 text-sm text-muted-foreground">Log received medications individually, then complete an inventory check.</p>

      <Tabs defaultValue="receive" className="mt-6">
        <TabsList>
          <TabsTrigger value="receive" data-testid="inv-tab-receive">Receiving</TabsTrigger>
          <TabsTrigger value="log" data-testid="inv-tab-log">Inventory Log</TabsTrigger>
        </TabsList>

        <TabsContent value="receive" className="mt-4">
          <form onSubmit={addItem} className="grid gap-3 rounded-xl border border-border/60 bg-card p-5 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-1.5 lg:col-span-2"><Label className="text-xs">Medication name</Label><Input data-testid="inv-medication" value={form.medication_name} onChange={set("medication_name")} required /></div>
            <div className="space-y-1.5"><Label className="text-xs">Unit</Label><Input data-testid="inv-unit" value={form.unit} onChange={set("unit")} required /></div>
            <div className="space-y-1.5"><Label className="text-xs">NDC</Label><Input data-testid="inv-ndc" value={form.ndc} onChange={set("ndc")} required /></div>
            <div className="space-y-1.5"><Label className="text-xs">Company</Label><Input data-testid="inv-company" value={form.company_name} onChange={set("company_name")} required /></div>
            <div className="lg:col-span-5"><Button type="submit" disabled={loading} data-testid="inv-add" className="rounded-full"><Plus className="mr-1 h-4 w-4" /> Add Medication</Button></div>
          </form>

          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{items.length} item(s) currently received</p>
            <Button onClick={completeLog} variant="outline" className="rounded-full" data-testid="inv-complete-check"><ClipboardCheck className="mr-1 h-4 w-4" /> Complete Inventory Check</Button>
          </div>

          <div className="mt-3 overflow-hidden rounded-xl border border-border/60 bg-card">
            <Table>
              <TableHeader><TableRow><TableHead>Received</TableHead><TableHead>Medication</TableHead><TableHead>Unit</TableHead><TableHead>NDC</TableHead><TableHead>Company</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.length === 0 ? <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No items received yet.</TableCell></TableRow>
                  : items.map((it) => (
                    <TableRow key={it.id} data-testid={`inv-row-${it.id}`}>
                      <TableCell className="whitespace-nowrap text-sm">{format(new Date(it.created_at), "MMM d, h:mm a")}</TableCell>
                      <TableCell className="font-medium">{it.medication_name}</TableCell>
                      <TableCell>{it.unit}</TableCell><TableCell className="text-muted-foreground">{it.ndc}</TableCell><TableCell>{it.company_name}</TableCell>
                      <TableCell className="text-right"><DeleteButton onConfirm={() => delItem(it.id)} label="this item" testId={`inv-delete-${it.id}`} /></TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="log" className="mt-4">
          {logs.length === 0 ? <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">No inventory checks completed yet.</div>
            : <div className="space-y-3">
              {logs.map((l) => (
                <div key={l.id} className="rounded-xl border border-border/60 bg-card p-5" data-testid={`inv-log-${l.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-heading font-bold">Inventory Check</p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{format(new Date(l.completed_at), "MMM d, yyyy h:mm a")}</span>
                      <DeleteButton onConfirm={() => delLog(l.id)} label="this inventory check" testId={`inv-log-delete-${l.id}`} />
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{l.item_count} item(s) counted</p>
                </div>
              ))}
            </div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
